import moment from "moment";

import type { UserInteractionsContract } from "@btnet/services-btnet/handlers/models/userInteractionsContract";

import { type IBaseEntity } from "types/apiResponse/apiResponse";
import { type BTLoginTypes, DiscussionsLinkedTypes, type MediaType } from "types/enum";

import { type IListRowData, ListEntity } from "utilities/list/list.types";

import {
    EditorContent,
    type IEditorApiType,
} from "commonComponents/btWrappers/editor/editor.types";

import type { default as CommentListJson } from "entity/comment/CommentList/CommentList.api.json";

type CommentsListEntityApiData = typeof CommentListJson;

interface ICommentItemData {
    commentId: number;
    jobsiteId: number;
    builderId: number;
    linkedType: number;
    linkedId: number;
    userFullName: string;
    sourceTitle: string;
    sourceType: string;
    commentDate: string;
    commentDateUtc?: string;
    commentText: string;
    commentJson?: IEditorApiType | null;
    commentCount: number;
    canDelete: boolean;
    showOwner: boolean;
    showSubs: boolean;
    mediaData: IMediaDataInput | null;
    documentLink: string;
    isUserComment: boolean;
    addedByID?: number;
    loginType: number;
    vimeoId: number;
    jobName: string;
    documentInstanceId?: number;
    mediaType?: number;
    linkedExternalId?: string;
    reactions?: UserInteractionsContract[];
}

interface IMediaDataInput {
    FullSize: string;
    OriginalFile: string;
    Thumb: string;
    Attachment: string;
    Description: string;
    HasAnnotations: boolean;
    is360Media: boolean;
    docId?: number;
    jobId?: number;
}

class MediaData {
    constructor(mediaData: IMediaDataInput) {
        this.fullSize = mediaData.FullSize;
        this.originalFile = mediaData.OriginalFile;
        this.thumb = mediaData.Thumb;
        this.attachment = mediaData.Attachment;
        this.description = mediaData.Description;
        this.hasAnnotations = mediaData.HasAnnotations;
        this.is360Media = mediaData.is360Media;
        this.id = mediaData.docId || undefined;
        this.jobId = mediaData.jobId || undefined;
    }

    fullSize: string;
    originalFile: string;
    thumb: string;
    attachment: string;
    description: string;
    hasAnnotations: boolean;
    is360Media: boolean;
    id?: number;
    jobId?: number;
}
export class CommentListEntity extends ListEntity {
    constructor(data: CommentsListEntityApiData) {
        super(data);
        const comments = (data.data as unknown as ICommentItemData[]).map(
            (li) => new CommentEntry(li),
        );
        this.data = comments.filter((com) => com.linkedType !== DiscussionsLinkedTypes.Receipts);
    }
    data: CommentEntry[];
}

export class CommentEntry implements IBaseEntity, IListRowData {
    constructor(data: ICommentItemData) {
        this.id = data.commentId;
        this.jobId = data.jobsiteId;
        this.builderId = data.builderId;

        this.linkedType = data.linkedType;
        this.linkedId = data.linkedId;
        this.userFullName = data.userFullName;
        this.sourceTitle = data.sourceTitle;
        this.sourceType = data.sourceType;
        this.commentDate = moment(data.commentDate);
        this.commentDateUtc = moment(data.commentDateUtc);
        this.commentText = data.commentText;
        this.commentJson = data.commentJson ? new EditorContent(data.commentJson) : null;
        this.commentCount = data.commentCount;
        this.canDelete = data.canDelete;
        this.canShowOwner = data.showOwner;
        this.canShowSubs = data.showSubs;
        this.mediaData = data.mediaData && new MediaData(data.mediaData);
        this.documentLink = data.documentLink;
        this.isUserComment = data.isUserComment;
        this.addedByID = data.addedByID;
        this.loginType = data.loginType;
        this.vimeoId = data.vimeoId;
        this.jobName = data.jobName;
        this.documentInstanceId =
            data.documentInstanceId && data.documentInstanceId !== 0
                ? data.documentInstanceId
                : undefined;
        this.mediaType = data.mediaType && data.mediaType !== 0 ? data.mediaType : undefined;
        this.linkedExternalId = data.linkedExternalId ?? undefined;
        this.reactions = [];
        if (data.reactions) {
            this.reactions = data.reactions.map((r: UserInteractionsContract) => ({
                ...r,
                readDateUtc: moment(r.readDateUtc),
                reactDateUtc: moment(r.reactDateUtc),
            })) as unknown as UserInteractionsContract[];
        }
    }

    // IBaseEntity
    id: number;
    jobId: number;
    builderId: number;

    linkedType: number;
    linkedId: number;
    userFullName: string;
    sourceTitle: string;
    sourceType: string;
    commentDate: moment.Moment;
    commentDateUtc: moment.Moment;
    commentText: string;
    commentJson: EditorContent | null;
    // commentCount is actually AdditionalCommentCount. It is the number of comments in addtion to the one being displayed in the list.
    commentCount: number;
    canDelete: boolean;
    canShowOwner: boolean;
    canShowSubs: boolean;
    mediaData: MediaData | null;
    documentLink: string;
    isUserComment: boolean;
    addedByID: number | undefined;
    loginType: BTLoginTypes;
    vimeoId: number;
    jobName: string;
    documentInstanceId: number | undefined;
    mediaType: MediaType | undefined;
    linkedExternalId: string | undefined;

    reactions: UserInteractionsContract[];
}

export enum CommentListTabKeys {
    Conversation = 1,
    Comment = 2,
}
